import React from 'react';
import ReactMarkdown from 'react-markdown';
import { Prism as SyntaxHighlighter } from 'react-syntax-highlighter';
import { oneDark } from 'react-syntax-highlighter/dist/cjs/styles/prism';
import rehypeRaw from 'rehype-raw';
import remarkGfm from 'remark-gfm';

interface Props {
  documentContent: string;
}
export default function FormattedMarkdownDisplay({ documentContent }: Props) {
  return (
    <div className='max-w-none border-none shadow-none bg-transparent'>
      <ReactMarkdown
        remarkPlugins={[remarkGfm]}
        rehypePlugins={[rehypeRaw]}
        disallowedElements={[
          'script',
          'iframe',
          'object',
          'embed',
          'form',
          'button',
          'input',
          'select',
          'textarea',
          'style',
          'canvas',
        ]}
        unwrapDisallowed={true}
        components={{
          h1: ({ node, ...props }) => <h1 className='text-3xl font-bold mb-4' {...props} />,
          h2: ({ node, ...props }) => <h2 className='text-2xl font-bold mb-3' {...props} />,
          h3: ({ node, ...props }) => <h3 className='text-xl font-bold mb-2' {...props} />,
          p: ({ node, ...props }) => <p className='mb-4 whitespace-pre-wrap' {...props} />,
          ul: ({ node, ...props }) => <ul className='list-disc pl-6 mb-4' {...props} />,
          ol: ({ node, ...props }) => <ol className='list-decimal pl-6 mb-4' {...props} />,
          li: ({ node, ...props }) => <li className='text-sm font-normal mb-1' {...props} />,
          table: ({ node, ...props }) => (
            <div className='overflow-x-auto w-full mb-4'>
              <table className='border-collapse table-auto min-w-full' {...props} />
            </div>
          ),
          th: ({ node, ...props }) => (
            <th className='border border-gray-300 px-4 py-2 bg-surface_gray_50' {...props} />
          ),
          td: ({ node, ...props }) => (
            <td className='border border-gray-300 px-4 py-2' {...props} />
          ),
          a: ({ node, ...props }) => <a className='text-blue-600 hover:underline' {...props} />,
          blockquote: ({ node, ...props }) => (
            <blockquote
              className='border-l-4 border-gray-200 pl-4 italic my-4 text-sm font-normal'
              {...props}
            />
          ),
          code(props: any) {
            const { className, children, ref, ...rest } = props;
            const match = /language-(\w+)/.exec(className || '');
            const language = match ? match[1] : '';
            const isInline = !match && !className;

            if (!isInline && language) {
              return (
                <div className='rounded-lg overflow-hidden my-4 bg-[#282c34]'>
                  <div className='bg-[#21252b] text-gray-200 text-sm px-4 py-2 flex justify-between items-center'>
                    <span className='font-mono'>{language}</span>
                    <button
                      onClick={() => {
                        const code = String(children).replace(/\n$/, '');
                        navigator.clipboard.writeText(code);
                      }}
                      className='text-gray-400 hover:text-white text-xs py-1 px-2 rounded transition-colors'
                      title='Copy code'
                      aria-label='Copy code to clipboard'
                    >
                      Copy
                    </button>
                  </div>
                  <SyntaxHighlighter
                    style={
                      {
                        ...oneDark,
                        'code[class*="language-"]': {
                          color: '#e3e3e3',
                        },
                        variable: {
                          color: '#e6e6e6',
                        },
                        keyword: {
                          color: '#c678dd',
                        },
                        function: {
                          color: '#61afef',
                        },
                        string: {
                          color: '#98c379',
                        },
                        number: {
                          color: '#d19a66',
                        },
                        operator: {
                          color: '#56b6c2',
                        },
                        comment: {
                          color: '#7f848e',
                        },
                      } as any
                    }
                    language={language}
                    PreTag='div'
                    showLineNumbers={true}
                    wrapLines={true}
                    customStyle={{
                      margin: 0,
                      borderTopLeftRadius: 0,
                      borderTopRightRadius: 0,
                      background: '#282c34',
                      fontSize: '14px',
                      lineHeight: '1.5',
                      color: '#e3e3e3',
                    }}
                    lineProps={(lineNumber) => {
                      const code = String(children);
                      const lines = code.split('\n');
                      const line = lines[lineNumber - 1] || '';

                      const hasError =
                        (line.match(/"/g) || []).length % 2 !== 0 ||
                        (line.match(/'/g) || []).length % 2 !== 0 ||
                        (line.match(/\{/g) || []).length !== (line.match(/\}/g) || []).length ||
                        (line.match(/\(/g) || []).length !== (line.match(/\)/g) || []).length ||
                        (line.match(/\[/g) || []).length !== (line.match(/\]/g) || []).length ||
                        /\b(const|let|var)\s*([a-zA-Z_$][a-zA-Z0-9_$]*)\s*(?!=)/.test(line) ||
                        (/[^=!<>]=[^=]/.test(line) && !/(\+=|-=|\*=|\/=|%=|&&=|\|\|=)/.test(line));

                      return {
                        style: {
                          display: 'block',
                          position: 'relative',
                          textDecoration: hasError ? 'underline wavy #ff6b6b' : 'none',
                          textDecorationThickness: '2px',
                          textUnderlineOffset: '3px',
                        },
                        className: hasError ? 'syntax-error' : undefined,
                      };
                    }}
                    codeTagProps={{
                      style: {
                        fontFamily:
                          'ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, "Liberation Mono", "Courier New", monospace',
                        color: '#e3e3e3',
                      },
                    }}
                    lineNumberStyle={{
                      color: '#636d83',
                      paddingRight: '1em',
                      userSelect: 'none',
                    }}
                  >
                    {String(children).replace(/\n$/, '')}
                  </SyntaxHighlighter>
                </div>
              );
            }

            return isInline ? (
              <code
                className='bg-[#282c34] text-[#e3e3e3] rounded px-1.5 py-0.5 font-mono text-sm'
                {...rest}
              >
                {children}
              </code>
            ) : (
              <div className='rounded-lg overflow-hidden my-4 bg-[#282c34]'>
                <SyntaxHighlighter
                  style={
                    {
                      ...oneDark,
                      'code[class*="language-"]': {
                        color: '#e3e3e3',
                      },
                      variable: {
                        color: '#e6e6e6',
                      },
                      keyword: {
                        color: '#c678dd',
                      },
                      function: {
                        color: '#61afef',
                      },
                      string: {
                        color: '#98c379',
                      },
                      number: {
                        color: '#d19a66',
                      },
                      operator: {
                        color: '#56b6c2',
                      },
                      comment: {
                        color: '#7f848e',
                      },
                    } as any
                  }
                  language='text'
                  PreTag='div'
                  showLineNumbers={true}
                  customStyle={{
                    margin: 0,
                    background: '#282c34',
                    fontSize: '14px',
                    lineHeight: '1.5',
                    color: '#e3e3e3',
                  }}
                  codeTagProps={{
                    style: {
                      fontFamily:
                        'ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, "Liberation Mono", "Courier New", monospace',
                      color: '#e3e3e3',
                    },
                  }}
                  lineNumberStyle={{
                    color: '#636d83',
                  }}
                >
                  {String(children).replace(/\n$/, '')}
                </SyntaxHighlighter>
              </div>
            );
          },
        }}
      >
        {documentContent}
      </ReactMarkdown>
    </div>
  );
}
